import LoadingTableItems from "@/components/LoadingTableItems";
import ConfirmDeleteButton from "@/components/ConfirmDeleteButton";
import EmptyTableRow from "@/components/EmptyTableRow";
import TableSortHeader from "@/components/TableSortHeader";
import TablePagination from "@/components/TablePagination";

import {notify} from "@/components/Notifications";
import {ProgramOverride, ProgramPage, ProgramSchedule, SortField, SortOrder,} from "@/hooks/usePrograms";
import {Pencil} from "lucide-react";

type Props = {
    content: ProgramPage;
    isLoading?: boolean;
    isError?: boolean;
    onCreate: () => void;
    onEdit?: (id: string) => void;
    onDelete?: (id: string) => Promise<unknown>;
    setPage: (p: number) => void;
    sortBy: SortField;
    sortOrder: SortOrder;
    onRequestSort: (field: SortField) => void;
};

function summarizeSchedules(schedules?: ProgramSchedule[]): string {
    if (!schedules || schedules.length === 0) return "—";
    // Group by time range + producerOverride to compress similar rows
    const key = (s: ProgramSchedule) =>
        `${s.startTime}-${s.endTime}-${s.producerOverride ?? ""}`;

    const groups = new Map<string, ProgramSchedule[]>();
    schedules.forEach((s) => {
        const k = key(s);
        groups.set(k, [...(groups.get(k) ?? []), s]);
    });

    const dayShort = (d: string) =>
        d.slice(0, 3).toLowerCase().replace(/^\w/, (c) => c.toUpperCase()); // Mon, Tue...

    // Sort by DayOfWeek ordering
    const order = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];
    const byDay = (a: ProgramSchedule, b: ProgramSchedule) =>
        order.indexOf(a.dayOfWeek) - order.indexOf(b.dayOfWeek);

    const parts: string[] = [];
    [...groups.entries()].forEach(([k, arr]) => {
        const sorted = arr.slice().sort(byDay);
        const days = sorted.map((s) => dayShort(s.dayOfWeek));
        // Collapse consecutive day ranges like Mon–Fri
        const compressed = compressDays(days);
        const [startTime, endTime, prod] = k.split("-");
        const label =
            (prod ? `${compressed} ${startTime}–${endTime} (${prod})` : `${compressed} ${startTime}–${endTime}`);
        parts.push(label);
    });

    return parts.join("; ");
}

function compressDays(days: string[]): string {
    if (days.length === 0) return "";
    const order = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const idx = (d: string) => order.indexOf(d);
    const sorted = days.slice().sort((a, b) => idx(a) - idx(b));

    // Build ranges
    const ranges: Array<{ start: string, end: string }> = [];
    let start = sorted[0];
    let prev = sorted[0];

    for (let i = 1; i < sorted.length; i++) {
        const cur = sorted[i];
        if (idx(cur) === idx(prev) + 1) {
            prev = cur;
            continue;
        }
        ranges.push({start, end: prev});
        start = prev = cur;
    }
    ranges.push({start, end: prev});

    return ranges
        .map(r => (r.start === r.end ? r.start : `${r.start}–${r.end}`))
        .join(", ");
}

function summarizeNextOverride(overrides?: ProgramOverride[]): string {
    if (!overrides || overrides.length === 0) return "—";
    // show soonest override date (ascending)
    const sorted = overrides
        .slice()
        .sort((a, b) => a.date.localeCompare(b.date));
    const o = sorted[0];
    // Example: 2025-10-19 09:00–12:00 (Guest Host)
    const prod = o.producerOverride ? ` (${o.producerOverride})` : "";
    return `${o.date} ${o.startTime}–${o.endTime}${prod}`;
}

export default function ProgramTable({
                                         content,
                                         isLoading,
                                         isError,
                                         setPage,
                                         onCreate,
                                         onEdit,
                                         onDelete,
                                         sortBy,
                                         sortOrder,
                                         onRequestSort,
                                     }: Props) {
    if (isLoading) return <LoadingTableItems/>;
    if (isError)
        return <p className="mt-6 text-sm text-red-600">Failed to load programs.</p>;

    return (
        <div className="mt-8 flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                    <div className="overflow-hidden shadow-sm outline-1 outline-black/5 sm:rounded-lg">
                        <table className="relative min-w-full divide-y divide-gray-300">
                            <thead className="bg-gray-50">
                            <tr>
                                {/* Photo (not sortable) */}
                                <th scope="col"
                                    className="py-3.5 pl-4 pr-3 sm:pl-6 text-left text-sm font-semibold text-blue-900">
                                    Photo
                                </th>

                                <TableSortHeader<SortField>
                                    field="title"
                                    activeField={sortBy}
                                    order={sortOrder}
                                    onSort={onRequestSort}
                                    className="py-3.5 pr-3 pl-4 sm:pl-6"
                                    thProps={{scope: "col"}}
                                >
                                    Title
                                </TableSortHeader>

                                <TableSortHeader<SortField>
                                    field="producer"
                                    activeField={sortBy}
                                    order={sortOrder}
                                    onSort={onRequestSort}
                                    thProps={{scope: "col"}}
                                >
                                    Producer
                                </TableSortHeader>

                                {/* Non-sortable helper columns */}
                                <th
                                    scope="col"
                                    className="px-3 py-3.5 text-left text-sm font-semibold text-blue-900"
                                >
                                    Schedules
                                </th>

                                <th
                                    scope="col"
                                    className="px-3 py-3.5 text-left text-sm font-semibold text-blue-900"
                                >
                                    Next Override
                                </th>

                                <th className="px-3 py-3.5 text-right text-sm font-semibold text-blue-900">
                                    <span className="sr-only">Actions</span>
                                </th>
                            </tr>
                            </thead>

                            <tbody className="divide-y divide-gray-200 bg-white">
                            {content.items.map((i) => (
                                <tr key={i.id}>

                                    {/* Photo */}
                                    <td className="py-4 pl-4 pr-3 sm:pl-6 whitespace-nowrap">
                                        <div
                                            className="h-10 w-10 overflow-hidden rounded-full ring-1 ring-gray-200 bg-gray-50">
                                            {/* eslint-disable-next-line @next/next/no-img-element */}
                                            <img
                                                src={i.imageUrl}
                                                alt={`${i.title} photo`}
                                                className="h-full w-full object-cover"
                                                onError={(e) => {
                                                    (e.currentTarget as HTMLImageElement).src =
                                                        "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='80' height='80'><rect width='100%' height='100%' fill='%23f3f4f6'/></svg>";
                                                }}
                                            />
                                        </div>
                                    </td>

                                    <td className="py-4 pr-3 pl-4 text-sm font-medium text-blue-900 sm:pl-6 whitespace-normal">
                                        {i.title}
                                    </td>

                                    <td className="px-3 py-4 text-sm text-gray-500 whitespace-normal">
                                        {i.producer}
                                    </td>

                                    <td className="px-3 py-4 text-sm text-gray-500 whitespace-normal">
                                        {summarizeSchedules((i as any).schedules)}
                                    </td>

                                    <td className="px-3 py-4 text-sm text-gray-500 whitespace-normal">
                                        {summarizeNextOverride((i as any).overrides)}
                                    </td>

                                    <td className="py-4 pr-4 pl-3 text-right text-sm font-medium whitespace-nowrap sm:pr-6">
                                        <div className="inline-flex items-center gap-4">
                                            {/* Edit */}
                                            {onEdit && (
                                                <button
                                                    type="button"
                                                    onClick={() => onEdit(i.id)}
                                                    className="text-yellow-600 hover:text-yellow-500"
                                                    aria-label={`Edit ${i.title}`}
                                                >
                                                    <Pencil/>
                                                </button>
                                            )}

                                            {/* Delete */}
                                            {onDelete && (
                                                <ConfirmDeleteButton
                                                    onDelete={() => onDelete(i.id)}
                                                    itemLabel="Program"
                                                    itemName={i.title}
                                                    onSuccess={() => notify.success("Program deleted.")}
                                                    className="text-red-600 hover:text-red-500"
                                                />
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}

                            {content.items.length === 0 && (
                                <EmptyTableRow
                                    colSpan={6}
                                    title="No programs found"
                                    description='Click “Add Program” to create your first.'
                                    action={{label: "Add Program", onClick: onCreate}}
                                    size="lg"
                                />
                            )}
                            </tbody>
                        </table>

                        <TablePagination
                            page={content.page}
                            size={content.size}
                            total={content.total}
                            onPage={setPage}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
